import { SelectionModel } from '@angular/cdk/collections';
import { Component, computed, effect, inject, input, model, output, signal, untracked } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Sort } from '@angular/material/sort';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { GridstackHeaderItem } from '@models/common';
import { ListResponse } from '@models/helpers';
import { GlobalFilterKeys } from '@models/module/fields/enums';
import { DataListType } from '@models/module/fields/main/dataListFieldModel';
import { GridSearchEntityInstance } from '@models/module/grid';
import { GridFieldsModel } from '@models/module/main';
import { DataListViewAction } from '@models/module/main/dataListViewAction';
import { ScreenTypeEnum } from '@module-definition/screen/common';
import { CalendarViewComponent } from '@module-instance/calendar/calendar-view/calendar-view.component';
import { DataCardsViewComponent } from '@module-instance/data-card/data-cards-view/data-cards-view.component';
import {
  DmActionEventType,
  DmFiltersToApply,
  DmQueryParams,
  DmService,
  DmTableContext,
  DmTableDataResponse,
  DmTableOptions,
  ReloadDm,
} from '@module-instance/dynamic-module/common';
import { GANTT_DATE_FIELDS_TYPE_MISMATCH_ERROR } from '@module-instance/gantt/common';
import { GanttViewComponent } from '@module-instance/gantt/gantt-view.component';
import { TranslateModule } from '@ngx-translate/core';
import {
  DataListCommandFilterType,
  DataListWrapper,
  EntityTableRow,
  FiltersToApply,
  FooterRow,
  TableColumn,
  TableDataService,
} from '@shared/components/data-list/common';
import { DataListModule } from '@shared/components/data-list/data-list.module';
import { GridstackHeaderComponent } from '@shared/components/data-list/gridstack-header/gridstack-header.component';
import { DEFAULT_INFINITE_PAGE, PAGE_SIZE_OPTIONS } from '@shared/consts';
import { DropdownActionsEnum } from '@shared/consts/dropdown.consts';
import { SafeHtmlPipe } from '@shared/pipes/safe-html.pipe';
import { UserStore } from '@signal-store/user.store';
import { BREAKPOINTS } from 'src/app-breakpoints.module';

@Component({
  selector: 'cipo-dynamic-module-table',
  templateUrl: './dynamic-module-table.component.html',
  imports: [
    DataListModule,
    GridstackHeaderComponent,
    FlexLayoutModule,
    MatProgressSpinnerModule,
    CalendarViewComponent,
    GanttViewComponent,
    DataCardsViewComponent,
    TranslateModule,
    SafeHtmlPipe,
  ],
})
export class DynamicModuleTableComponent extends DataListWrapper<EntityTableRow> {
  private userStore = inject(UserStore);
  private tableDataService = inject(TableDataService);
  dmService = inject(DmService);

  isInfiniteScroll = computed(() => this.userStore.userData().loadMore);
  isSmallscreen = window.innerWidth < BREAKPOINTS.sm;
  readonly rowClickActionId = DropdownActionsEnum.VIEW;
  private gridFields: GridFieldsModel;
  ganttGridFields = signal<GridFieldsModel>(null);
  /** Bumped to refetch Gantt data without a redundant search/fields emission race. */
  ganttDataReloadToken = signal(0);
  errorMessage = signal<string>(null);

  searchParams = computed(() => {
    const options = this.tableOptions();
    return options ? this.dmService.getSearchParams(options, this.filtersToApply() as DmFiltersToApply) : null;
  });

  context = input<DmTableContext>(null);
  calendarActions = input<DataListViewAction[]>();
  cardActions = input<DataListViewAction[]>([]);
  reload = model<ReloadDm>();

  override selection = model<SelectionModel<EntityTableRow>>(null);
  override filtersToApply = model<DmFiltersToApply>({} as DmFiltersToApply);
  // override base signal with model so parent can read loading state
  override isLoading = model(false);
  override tableOptions = model<DmTableOptions>();

  fieldsLoaded = output<GridFieldsModel>();
  rowsLoaded = output<GridSearchEntityInstance[]>();
  actionSelected = output<DmActionEventType>();
  calendarEventClicked = output<GridSearchEntityInstance>();
  cardClicked = output<GridSearchEntityInstance>();

  readonly screenTypeEnum = ScreenTypeEnum;

  override tableColumns = signal<TableColumn[]>([]);
  loadGridSearchOnDataListTypes = [DataListType.Module, DataListType.Relation, DataListType.Card];
  DATA_LIST_TYPE = DataListType;
  filters = signal<DataListCommandFilterType[]>([]);
  selectedGlobalFilter = signal<GlobalFilterKeys>(null);
  gridstackHeaderItems = signal<GridstackHeaderItem[]>([]);
  gridstakHeaderData = signal<Record<string, unknown>>({});
  tableFooterRows = signal<FooterRow[]>(null);
  updatedRows = signal<EntityTableRow[]>([]);

  viewType = computed(() => this.context()?.dataListType ?? DataListType.Module);
  moduleId = computed(() => this.context()?.moduleId);
  contractId = computed(() => this.context()?.contractId);
  relationProps = computed(() => this.context()?.relationProps);

  constructor() {
    super();

    effect(() => {
      const tableOptions = this.tableOptions();
      if (!tableOptions) {
        untracked(() => {
          const pageSize = this.isInfiniteScroll() ? DEFAULT_INFINITE_PAGE : PAGE_SIZE_OPTIONS.default[2];
          this.tableOptions.set({
            clickableRows: true,
            selectable: !this.isSmallscreen,
            serverPaginationSort: true,
            pagination: {
              pageIndex: 0,
              pageSize,
              pageSizeOptions: PAGE_SIZE_OPTIONS.default,
            },
          });
          this.pageSize.set(pageSize);
        });
      }
    });

    // Child model updates do not propagate back to this signal on Gantt destroy; clear here when leaving Gantt.
    effect(() => {
      if (this.viewType() !== DataListType.Gantt && this.errorMessage() === GANTT_DATE_FIELDS_TYPE_MISMATCH_ERROR) {
        this.errorMessage.set(null);
      }
    });

    effect(onCleanup => {
      const context = this.context();
      if (!context?.moduleId || context.workflowId == null) {
        return;
      }

      const subscription = untracked(() => {
        this.resetBeforeLoadTableFields();
        return this.loadContext(context).subscribe(response => {
          if (this.isTableDataResponse(response)) {
            this.applyInitialTableData(response);
            return;
          }
          this.applyGridFields(response);
        });
      });

      onCleanup(() => subscription.unsubscribe());
    });

    effect(() => {
      const reload = this.reload();
      if (!reload) {
        return;
      }

      untracked(() => this.handleReload(reload));
    });
  }

  private loadContext(context: DmTableContext): Observable<GridFieldsModel | DmTableDataResponse> {
    const params = this.getQueryParams(context);
    const customLoader = context.customGridFieldsLoader;
    if (customLoader) {
      return customLoader(params);
    }
    if (this.loadGridSearchOnDataListTypes.includes(context.dataListType ?? DataListType.Module)) {
      return this.dmService.getTableData({
        ...params,
        search: this.getInitialLoadSearchParams(),
        initialLoad: true,
      });
    }
    return this.dmService.getGridFields(params);
  }

  private handleReload(reload: ReloadDm) {
    if (reload.entityInstanceIds?.length) {
      if (this.loadGridSearchOnDataListTypes.includes(this.viewType())) {
        this.reloadTableRows(reload.entityInstanceIds);
      } else {
        this.onReloadTableData();
      }
      return;
    }

    if (reload.reloadData) {
      this.onReloadTableData();
    }
  }

  private getQueryParams(context = this.context()): DmQueryParams {
    const params = { ...(context ?? {}) };
    delete params.customGridFieldsLoader;
    delete params.version;
    return params;
  }

  protected override shouldFetchItems(): boolean {
    return !!this.context()?.moduleId && this.loadGridSearchOnDataListTypes.includes(this.viewType());
  }

  override loadItems(
    page: number,
    size: number,
    filters?: FiltersToApply,
    sort?: Sort,
  ): Observable<ListResponse<EntityTableRow>> {
    const options = untracked(() => this.tableOptions());
    if (!this.tableColumns().length || !options) {
      return of({ data: [] as EntityTableRow[], records: 0 });
    }
    const dmFilters = (filters ?? {}) as DmFiltersToApply;
    const searchParams = this.dmService.getSearchParams(
      { ...options, sort: sort || options.sort },
      dmFilters,
      page - 1,
    );
    return this.dmService
      .getTableData({
        ...this.getQueryParams(),
        search: searchParams,
      })
      .pipe(
        tap(({ data }) => {
          this.rowsLoaded.emit(data);
          if (page === 1 && this.viewType() === DataListType.Module) {
            this.loadHeaderFooterData();
          }
        }),
        map(({ data, records }) => ({
          data: this.tableDataService.mapTableRowsState(data, 'entity_template_id'),
          records,
        })),
      );
  }

  private resetBeforeLoadTableFields() {
    this.dmService.clearGridSearchDebugInfo();

    // Calendar/Gantt manage loading via child [(isLoading)] binding; only grid views use DataListWrapper fetch.
    if (this.loadGridSearchOnDataListTypes.includes(this.viewType())) {
      this.isLoading.set(true);
      this.items.set(undefined as EntityTableRow[]);
      this.tableColumns.set([]);
      this.gridstackHeaderItems.set([]);
      this.gridstakHeaderData.set({});
    } else if (this.viewType() === DataListType.Gantt) {
      this.ganttGridFields.set(null);
    }
    this.selection()?.clear();
    this.tableDataService.resetSort(this.tableOptions);
    this.sort.set(null);
    this.resetModuleSpecificFilters();
  }

  private applyGridFields(gridFields: GridFieldsModel, refreshTable = true) {
    this.gridFields = gridFields;
    this.ganttGridFields.set(gridFields);
    this.tableDataService.setGridFields(
      gridFields,
      this.tableColumns,
      this.gridstackHeaderItems,
      this.tableFooterRows,
      this.tableOptions,
      this.filters,
      true,
    );
    this.fieldsLoaded.emit(gridFields);
    if (refreshTable && this.loadGridSearchOnDataListTypes.includes(this.viewType())) {
      this.filtersToApply.update(({ showPresets, isCurrent }) => ({ showPresets, isCurrent }));
    } else if (this.viewType() === DataListType.Gantt) {
      this.ganttDataReloadToken.update(token => token + 1);
    } else if (this.viewType() === DataListType.Calendar) {
      this.refreshList();
    }
  }

  private isTableDataResponse(response: GridFieldsModel | DmTableDataResponse): response is DmTableDataResponse {
    return 'data' in response && 'records' in response;
  }

  private getInitialLoadSearchParams() {
    const options = untracked(() => this.tableOptions());
    const { showPresets, isCurrent } = this.filtersToApply() as DmFiltersToApply;
    return this.dmService.getSearchParams(options, { showPresets, isCurrent }, 0);
  }

  private applyInitialTableData({ data, records, gridFields, search }: DmTableDataResponse) {
    this.applyGridFields(gridFields, false);
    this.rowsLoaded.emit(data);
    this.items.set(this.tableDataService.mapTableRowsState(data, 'entity_template_id'));
    this.tableDataService.updatePaginationLength(this.tableOptions, records);
    this.tableOptions.update(options => ({
      ...options,
      pagination: {
        ...options.pagination,
        pageIndex: (search?.page ?? 1) - 1,
        pageSize: search?.pagesize ?? options.pagination.pageSize,
      },
    }));
    this.isLoading.set(false);
    if (this.viewType() === DataListType.Module) {
      this.loadHeaderFooterData();
    }
  }

  private onReloadTableData() {
    if (this.viewType() === DataListType.Gantt) {
      this.ganttDataReloadToken.update(token => token + 1);
      return;
    }
    this.refreshList();
  }

  private loadHeaderFooterData() {
    this.dmService.getAndSetHeaderFooterData(
      this.contractId(),
      this.moduleId(),
      this.relationProps(),
      this.gridFields,
      this.tableOptions(),
      this.filtersToApply() as DmFiltersToApply,
      this.gridstakHeaderData,
      this.tableFooterRows,
    );
  }

  private reloadTableRows(entityInstanceIds: number[]) {
    if (!entityInstanceIds?.length) {
      this.tableDataService.setDefaultSort(this.tableOptions, this.gridFields);
      this.filtersToApply.update(({ showPresets, isCurrent }) => ({ showPresets, isCurrent }));
      return;
    }
    const rows = entityInstanceIds.map(entityInstanceId => ({
      entity_instance_id: entityInstanceId,
      empty: true,
    }));
    this.updatedRows.set(rows);
    this.dmService
      .getMultiRowsData({
        contractId: this.contractId(),
        moduleId: this.moduleId(),
        relationProps: this.relationProps(),
        isCurrent: (this.filtersToApply() as DmFiltersToApply)?.isCurrent,
        entityInstanceIds,
      })
      .subscribe(({ data }) => {
        if (!data.length) {
          this.refreshList();
          return;
        }
        this.updatedRows.set(this.tableDataService.mapTableRowsState(data));
        this.loadHeaderFooterData();
      });
  }

  private resetModuleSpecificFilters() {
    const { criteria, filters, isGlobalSearch, isQuickFilter } = this.filtersToApply() as DmFiltersToApply;
    if (!criteria && !filters && !isGlobalSearch && !isQuickFilter) {
      return;
    }

    // The component instance is reused for /dm route changes, but searches and advanced filters belong to one module.
    this.filtersToApply.update(({ showPresets, isCurrent }) => ({ showPresets, isCurrent }));
  }
}
